import { Menu, ChevronDown } from "lucide-react";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { AgentChatAvatar } from "@/components/chat/agent-chat-avatar";

interface MobileTopBarProps {
  selectedAgentId: string | null;
  onOpenDrawer: () => void;
  onOpenAgentSheet: () => void;
}

export function MobileTopBar({
  selectedAgentId,
  onOpenDrawer,
  onOpenAgentSheet,
}: MobileTopBarProps) {
  const { tenantId, tenantName, tenantLogoUrl } = useTenant();
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const selectedAgent = agents.find((a) => a.id === selectedAgentId);

  return (
    <header
      className="glass-chrome mg-header sm:hidden sticky top-0 z-30 flex items-center gap-2 px-3 h-13 shrink-0"
      style={{ paddingTop: "env(safe-area-inset-top)", minHeight: 52 }}
    >
      {/* Hamburger → drawer */}
      <button onClick={onOpenDrawer} className="mg-icon-btn" aria-label="Open menu">
        <Menu size={18} />
      </button>

      {/* Tenant logo */}
      <div
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] text-accent overflow-hidden"
        style={{ background: "var(--mg-accent-soft, rgba(59,119,216,0.12))" }}
      >
        {tenantLogoUrl ? (
          <img src={tenantLogoUrl} alt={tenantName} className="h-full w-full object-cover" />
        ) : (
          <span className="text-xs font-bold">{tenantName?.[0]?.toUpperCase() || "?"}</span>
        )}
      </div>

      {/* Agent selector button (center, takes remaining space) */}
      <button
        onClick={onOpenAgentSheet}
        className="flex flex-1 min-w-0 items-center gap-2 rounded-full px-2 py-1.5 hover:bg-white/30 dark:hover:bg-white/10 text-left"
      >
        {selectedAgent ? (
          <>
            <AgentChatAvatar avatarUrl={selectedAgent.avatarUrl} name={selectedAgent.name} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-text-primary truncate">
                {selectedAgent.name}
              </p>
              {selectedAgent.title && (
                <p className="text-[10px] text-text-muted truncate">{selectedAgent.title}</p>
              )}
            </div>
            <ChevronDown size={14} className="text-text-muted shrink-0" />
          </>
        ) : (
          <>
            <span className="text-sm text-text-muted flex-1 truncate">Select agent</span>
            <ChevronDown size={14} className="text-text-muted shrink-0" />
          </>
        )}
      </button>
    </header>
  );
}
